<template>
  <UIContentWrap class="max-h-full h-screen bg-white w-full fonts-fallback p-0 overflow-hidden">
      <NestedRoutes />
  </UIContentWrap>
</template>

<script lang="ts">
import '@gohighlevel/ghl-ui/dist/style.css'
import './index.css'
import {
  defineComponent,
  watchEffect,
  computed,
  getTransitionRawChildren,
  getCurrentInstance,
  PropType,
  onBeforeUnmount,
  onBeforeMount,
} from 'vue'
import { useStore } from './store'
import { ActionTypes } from './store/actions'
import { useService } from './service'
import { useRouteRedirection } from './composable/manageRedirect'
import { setSentryUser } from './plugins/sentry'
import { ICustomValue, ICustomField } from './types'
import { setupPendo } from 'common/utils/pendo_config'
import { useI18n } from 'vue-i18n'
import Builder from './pages/Builder.vue'

import { builderState } from 'common/composable/manageBuilderState'
import { ITriggerLinks } from '../../email-builder/src/types'
import i18n from 'common/locales'
import { store } from './store'
import { EMAIL_PREVIEW_ROUTES_NAMES } from 'common/utils/const_helper'
import Header from './components/Header.vue'
import NestedRoutes from './pages/NestedRoutes.vue'
import { appState } from './../../common/composable/appState'
import {
    UIContentWrap
  } from '@gohighlevel/ghl-ui'
import clickAway from 'common/directives/clickAway'

export default defineComponent({
  name: 'container',
  components: { Builder, Header, NestedRoutes, UIContentWrap },
  props: {
    store: {
      type: Object as PropType<any>,
      required: true,
    },
    route: {
      type: Object as PropType<any>,
      required: true,
    },
    emitter: {
      type: Object as PropType<any>,
      required: true,
    },
    model: {
      type: Object as PropType<any>,
      required: true,
    },
  },
  setup(props, context) {
    const service = useService()
    let app = getCurrentInstance()?.appContext?.app
    app?.use(store)
    app?.provide('emitter', props.emitter)
    app?.use(i18n)
    app?.directive('click-outside', clickAway)

    const route = props.route
    app?.provide('route', route)
    const i18nInstance = useI18n()
    const storeLocal = useStore()
    const { routeBack } = useRouteRedirection()
    const isModalDialog = computed(() => props.model.isModalDialog)

    onBeforeMount(() => {
      appState.isEmailPreviewApp = true
      appState.selectedPage = EMAIL_PREVIEW_ROUTES_NAMES.EMAIL_TEMPLATE_BUILDER

      const { model } = props

      appState.locationId = model.locationId
      appState.templateId = model.emailId
      appState.folderId = model.folderId || route.query.folderId
      appState.isModalDialog = isModalDialog.value
      appState.cloneTemplate = model.cloneTemplate

      setLocales(model.locale)
      setSentryUser(model)
      storeLocal.dispatch(ActionTypes.UPDATE_AUTH_INFO, model)
      setupPendo({
        companyId: model.companyId,
        userId: model.userId,
        locationId: appState.locationId as string,
      })

      extractCustomVariables()
      builderState.isNewBuilder = model.isNewBuilder
      if (model?.customValues) builderState.customValues = model?.customValues
    })

    props?.emitter.on(
      'email_preview-content-ai-generated-content',
      (id: string) => {
        handleAIContent(id, 'content')
      }
    )

    props?.emitter.on(
      'email_preview-content-ai-generated-image',
      (id: string) => {
        handleAIContent(id, 'image')
      }
    )

    props?.emitter.on(
      'email_preview-quick-action-generated-session',
      (payload: any) => {
        handleQuickAction(payload)
      }
    )

    

    watchEffect(async () => {
      const emailId = appState.templateId

      if (emailId && storeLocal.state.userId) {
        try {
          const { data } = await service.fetchTemplate({
            id: appState.templateId as string,
            locationId: appState.locationId as string,
          })
          storeLocal.dispatch(ActionTypes.UPDATE_TEMPLATE, data)
          appState.templateName = data.name
        } catch (error) {
          console.error(error)
          if (!isModalDialog) routeBack()
        }
      }
    })

    function handleAIContent(id: string, type: string) {
      // @ts-expect-error
      storeLocal.state.builderState.then(child => {
        if (type === 'content') {
          child.call('content-ai-generated-content', id)
        } else {
          child.call('content-ai-generated-image', id)
        }
      })
      if (builderState.currentAiEditor === 'templateBackground') {
        builderState.contentAiIds.templateBackground = id
        builderState.contentUpdated = false
      }
    }

    const handleQuickAction = (payload: any) => {
      // @ts-expect-error
      storeLocal.state.builderState.then(child =>
        child.call('quick-action-generated-session', payload)
      )
    }

    async function extractCustomVariables() {
      let customValues: ICustomValue[] = []
      let customFields: ICustomField[] = []
      let triggerLinks: ITriggerLinks[] = []
      try {
        const { data } = await service.fetchCustomValues(
          appState.locationId as string
        )
        customValues = data['custom-values']
      } catch (error) {
        console.error(error)
      }
      try {
        const { data } = await service.fetchCustomFields(
          appState.locationId as string
        )
        customFields = data.customFields
      } catch (error) {
        console.error(error)
      }

      try {
        const { data } = await service.fetchTriggerLinks(
          appState.locationId as string
        )
        triggerLinks = data.links
      } catch (error) {
        console.error(error)
      }

      storeLocal.dispatch(ActionTypes.UPDATE_CUSTOM_VALUES, {
        customFields,
        customValues,
        triggerLinks,
      })
    }
    function setLocales(lang: string) {
      i18nInstance.locale.value = lang
    }
    onBeforeUnmount(() => {
      props?.emitter.off('email_preview-content-ai-generated-content')
      props?.emitter.off('email_preview-content-ai-generated-image')
      props?.emitter.off('email_preview-quick-action-generated-session')
      storeLocal.dispatch(ActionTypes.UPDATE_TEMPLATE, {
        name: '',
        openRate: '',
        bounceRate: '',
        archived: '',
        builderVersion: '',
        fromName: '',
        fromAddress: '',
        subjectLine: '',
        previewText: '',
        lastUpdated: '',
        thumbnailImage: '',
        updatedBy: '',
        locationId: '',
        previewUrl: '',
        type: '',
        rssFeedURL: '',
      })
      resetBuilderState()
      resetAppState()

    })

    function resetBuilderState() {
      builderState.loader = false
      builderState.saveAsTemplateLoading = false
      builderState.lastSavedOn = ''
      builderState.saveStatus = 'No unsaved changes'
      builderState.unsavedChanges = false
      builderState.historyQueueLength = 0
      builderState.historyPosition = -1
      builderState.hasRSSElement = false
      builderState.previewUrl = ''
      builderState.updatedAt = ''
      builderState.updatedABTest = false
      builderState.subjectLine = ''
      builderState.fromAddress = ''
      builderState.errorState = false
      builderState.mediaOpen = false
      builderState.contentAiIds = {} as any
      builderState.currentAiEditor = ''
      builderState.contentUpdated = true
      builderState.enableContentAI = false
      builderState.bulkReqInfo = {} as any
      builderState.isNewBuilder = false
      builderState.attachments = []
      builderState.logoUrl = ''
      builderState.openLayoutTemplates = false
      builderState.customValues = []
      builderState.currencySymbols = {} as any
      builderState.currency = 'USD'
      builderState.currentImageSlide = 0
    }
    function resetAppState () {
      appState.isEmailPreviewApp = false
      appState.selectedPage = ''
      appState.scheduleId = ''
      appState.locationId = ''
      appState.templateId = ''
      appState.templateName = ''
      appState.folderId = ''
      appState.isModalDialog = false
      appState.cloneTemplate = false
    }
    return {}
  },
})
</script>
<style>

html {
  overflow: hidden;
}

.py-6 {
  padding-top: 1.5rem;
  padding-bottom: 1.5rem;
}

.py-5 {
  padding-top: 1.25rem !important;
  padding-bottom: 1.25rem !important;
}

.space-y-6 {
  margin-top: 1.5rem;
}
.w-screen {
  width: 100vw;
}
.ui-notification {
  width: 333px;
  max-width: 333px;
  min-height: 60px;
  color: #000;
  font-size: 12px;
  line-height: 1;
  background-color: #fff;
  word-wrap: break-word;
  transition: all 0.4s;
  display: flex;
}

.fonts-fallback, #email-builder-action-menu .n-dropdown-option {
  font-family: 'Inter' !important;
  -webkit-font-smoothing: auto !important;
}

#emailPreview .n-button {
  font-family: 'Inter' !important;
  font-weight: 500 !important;
  -webkit-font-smoothing: auto !important;
}
.n-base-selection .n-base-selection-overlay {
  padding-left: 12px;
  padding-right: 26px;
}
.n-base-selection-label__render-label .n-base-selection-overlay {
  padding-left: 12px;
  padding-right: 26px;
}
</style>